import React from 'react';

export interface TextProps {
  /**
   * The variant determines both the semantic HTML element and the visual styling
   * - heading-1 through heading-6: Semantic headings (h1-h6)
   * - body-large, body, body-small: Paragraph text variants
   * - label: Form field labels
   * - caption: Small supplementary text
   * - error: Error messages
   * - success: Success messages
   * - muted: Secondary/tertiary text
   * - inline: Inline text (span)
   * - code: Inline code snippets
   */
  variant?:
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'heading-4'
    | 'heading-5'
    | 'heading-6'
    | 'body-large'
    | 'body'
    | 'body-small'
    | 'label'
    | 'caption'
    | 'error'
    | 'success'
    | 'muted'
    | 'inline'
    | 'code';
  /**
   * Override the rendered HTML element
   * By default, the variant determines the element:
   * - heading-* renders as h1-h6
   * - body-* renders as p
   * - label renders as label
   * - inline renders as span
   * - code renders as code
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'code' | 'div';
  /**
   * Text color variant
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'muted' | 'error' | 'success';
  /**
   * Make text bold (switches from MuseoSansRounded-300 to MuseoSansRounded-700)
   */
  bold?: boolean;
  /**
   * Text alignment
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Truncate text with ellipsis
   */
  truncate?: boolean;
  /**
   * Remove margin-bottom (useful for inline contexts like tables, breadcrumbs)
   */
  inline?: boolean;
  /**
   * Visually hidden but accessible to screen readers
   */
  srOnly?: boolean;
  /**
   * The text content
   */
  children: React.ReactNode;
  /**
   * HTML for attribute (for label elements)
   */
  htmlFor?: string;
  /**
   * HTML id attribute
   */
  id?: string;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * ARIA label
   */
  'aria-label'?: string;
  /**
   * ARIA described by
   */
  'aria-describedby'?: string;
  /**
   * ARIA live region
   */
  'aria-live'?: 'off' | 'polite' | 'assertive';
  /**
   * ARIA current state
   */
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | boolean;
}

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  as,
  color = 'inherit',
  bold = false,
  align = 'left',
  truncate = false,
  inline = false,
  srOnly = false,
  children,
  htmlFor,
  id,
  className = '',
  style,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-live': ariaLive,
  'aria-current': ariaCurrent,
}) => {
  // Determine the HTML element to render
  const getElement = (): string => {
    if (as) return as;

    // Default element based on variant
    switch (variant) {
      case 'heading-1':
        return 'h1';
      case 'heading-2':
        return 'h2';
      case 'heading-3':
        return 'h3';
      case 'heading-4':
        return 'h4';
      case 'heading-5':
        return 'h5';
      case 'heading-6':
        return 'h6';
      case 'label':
        return 'label';
      case 'inline':
      case 'muted':
        return 'span';
      case 'code':
        return 'code';
      default:
        return 'p';
    }
  };

  const element = getElement();

  // Base classes
  const baseClasses = 'hi-ui-text';

  // Variant classes
  const variantClasses = `hi-ui-text-${variant}`;

  // Color classes
  const colorClass = color !== 'inherit' ? `hi-ui-text-color-${color}` : '';

  // Bold class
  const boldClass = bold ? 'hi-ui-text-bold' : '';

  // Align classes
  const alignClass = align !== 'left' ? `hi-ui-text-align-${align}` : '';

  // Truncate class
  const truncateClass = truncate ? 'hi-ui-text-truncate' : '';

  // Screen reader only class
  const srOnlyClass = srOnly ? 'tw-sr-only' : '';

  const allClasses =
    `${baseClasses} ${variantClasses} ${colorClass} ${boldClass} ${alignClass} ${truncateClass} ${srOnlyClass} ${className}`.trim();

  // Apply inline prop to remove margin-bottom
  const inlineStyle = inline ? { marginBottom: 0, ...style } : style;

  const props = {
    className: allClasses,
    style: inlineStyle,
    id,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-live': ariaLive,
    'aria-current': ariaCurrent,
    ...(element === 'label' && htmlFor ? { htmlFor } : {}),
  };

  return React.createElement(element, props, children);
};
